/**
 * 连接验证 Hook
 *
 * 实现 React Flow 连接验证逻辑
 */

import { useCallback } from 'react';
import { type Connection, type Edge } from '@xyflow/react';
import type { WorkflowNode, ConditionNodeData } from '../types/workflow';

/**
 * 连接验证 Hook
 *
 * @param nodes 当前所有节点
 * @param edges 当前所有边
 */
export function useValidateConnection(
  nodes: WorkflowNode[],
  edges: Edge[]
) {
  const isValidConnection = useCallback(
    (connection: Edge | Connection) => {
      const { source, target, sourceHandle, targetHandle } = connection;

      // 1. 检查源节点和目标节点是否存在
      const sourceNode = nodes.find((n) => n.id === source);
      const targetNode = nodes.find((n) => n.id === target);

      if (!sourceNode || !targetNode) {
        return false;
      }

      // 2. 不允许自连接
      if (source === target) {
        return false;
      }

      // 3. 检查是否重复连接
      const existingEdge = edges.find(
        (e) =>
          e.source === source &&
          e.target === target &&
          e.sourceHandle === sourceHandle &&
          e.targetHandle === targetHandle
      );

      if (existingEdge) {
        return false;
      }

      // 4. 特殊节点连接限制
      // 开始节点只能作为源（没有输入连接点）
      if (targetNode.type === 'start') {
        return false;
      }

      // 注释节点不能被连接
      if (sourceNode.type === 'note' || targetNode.type === 'note') {
        return false;
      }

      // 循环容器不能直接连接（方案2：循环容器没有连接点，子节点直接连接）
      if (sourceNode.type === 'loop' || targetNode.type === 'loop') {
        return false;
      }

      // 5. 允许跨层级连接（循环容器内的子节点可以直接连接外部节点）
      // 不再限制 sourceParentId !== targetParentId 的情况

      // 6. 条件分支节点特殊处理
      // 条件分支可以有多个输出连接点（每个分支一个）
      // 这在 React Flow 中通过 sourceHandle 区分

      return true;
    },
    [nodes, edges]
  );

  /**
   * 获取节点允许的连接点数量
   */
  const getHandleCount = useCallback(
    (nodeId: string, handleType: 'source' | 'target') => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return 0;

      // 循环容器没有连接点（方案2）
      if (node.type === 'loop') {
        return 0;
      }

      // 目标连接点（输入）
      if (handleType === 'target') {
        // 开始节点和注释节点没有输入连接点
        if (node.type === 'start' || node.type === 'note') {
          return 0;
        }
        return 1;
      }

      // 源连接点（输出）
      if (node.type === 'note') {
        return 0;
      }

      // 条件分支节点有多个输出连接点
      if (node.type === 'condition' && node.data.type === 'condition') {
        const data = node.data as ConditionNodeData;
        return data.branches?.length || 0;
      }

      return 1;
    },
    [nodes]
  );

  /**
   * 获取节点允许连接的目标类型
   */
  const getAllowedTargetTypes = useCallback(
    (sourceNodeId: string) => {
      const sourceNode = nodes.find((n) => n.id === sourceNodeId);
      if (!sourceNode) return [];

      // 注释节点不能连接任何节点
      if (sourceNode.type === 'note') return [];

      // 开始节点可以连接到 agent, condition, info（循环容器没有连接点）
      if (sourceNode.type === 'start') {
        return ['agent', 'condition', 'info'];
      }

      // 其他节点可以连接到 agent, condition, info
      return ['agent', 'condition', 'info'];
    },
    [nodes]
  );

  return {
    isValidConnection,
    getHandleCount,
    getAllowedTargetTypes,
  };
}

export default useValidateConnection;
